import { useAuth } from "../context/AuthContext.js";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import api from "../api/api.js";
import MainLayout from "../sidebar/Sidebar.js";
import { motion, AnimatePresence } from "framer-motion";
import { ProjectDropdown } from "../components/ProjectDropdown.js";
import { useSearchInvalidation, SEARCH_QUERY_KEYS } from "../hooks/useSearchInvalidation.js";

function AnimatedCounter({ value, duration = 1000 }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      setCount(Math.floor(progress * value));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);

  return <span>{count}</span>;
}

function CircularProgress({ percentage, color, size = 120 }) {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="currentColor"
        strokeWidth="8"
        fill="none"
        className="text-gray-200"
      />
      <motion.circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={color}
        strokeWidth="8"
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={circumference}
        animate={{ strokeDashoffset: offset }}
        transition={{ duration: 1.5, ease: "easeOut" }}
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function ProjectsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { invalidateCategory } = useSearchInvalidation();

  const [location, setLocation] = useState("Projects");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeStatus, setActiveStatus] = useState("All");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    project_name: "",
    project_number: "",
  });

  const [error, setError] = useState(null);

  const {
    data: projects = [],
    isLoading: loading,
    isError,
  } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.projects(),
    queryFn: async () => {
      const response = await api.get("/projects/");
      console.log(response.data.length);
      return response.data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  const createProjectMutation = useMutation({
    mutationFn: async (projectData) => {
      const response = await api.post("/projects/", {
        project_name: projectData.project_name.trim(),
        project_number: Number.parseInt(projectData.project_number),
      });
      return response.data;
    },
    onSuccess: (newProject) => {
      // Optimistic update with standardized query key
      queryClient.setQueryData(SEARCH_QUERY_KEYS.projects(), (oldProjects) => [
        newProject,
        ...(oldProjects || []),
      ]);
      // Invalidate to ensure global search index is updated
      invalidateCategory("projects");

      setFormData({ project_name: "", project_number: "" });
      setShowCreateModal(false);
    },
    onError: (err) => {
      if (err.response?.status === 409) {
        setError("This project name is already in use.");
      } else if (err.response?.status === 400) {
        setError(
          "Project number already exists for this user or invalid data provided."
        );
      } else {
        setError("Failed to create project. Please try again.");
      }
    },
  });

  function onHoverLoadProjectDetail(projectId) {
    queryClient.prefetchQuery({
      queryKey: ["lineItems", projectId],
      queryFn: async () => {
        const response = await api.get(`/projects/${projectId}/line-items/`);
        return response.data;
      },
      staleTime: 5 * 60 * 1000,
    });

    queryClient.prefetchQuery({
      queryKey: ["projectTodos", projectId],
      queryFn: async () => {
        const response = await api.get(`/todos/?project_id=${projectId}`);
        return response.data;
      },
      staleTime: 5 * 60 * 1000,
    });
  }

  const deleteProjectMutation = useMutation({
    mutationFn: async (projectId) => {
      await api.delete(`/projects/${projectId}`);
      return projectId;
    },
    onSuccess: (deletedProjectId) => {
      // Optimistic update with standardized query key
      queryClient.setQueryData(SEARCH_QUERY_KEYS.projects(), (oldProjects) =>
        (oldProjects || []).filter((proj) => proj.id !== deletedProjectId)
      );
      // Invalidate to ensure global search index is updated
      invalidateCategory("projects");
      setDeleteModalOpen(false);
      setProjectToDelete(null);
    },
    onError: (error) => {
      console.error("Failed to delete project:", error);
      alert("Could not delete the project. Please try again.");
      setDeleteModalOpen(false);
      setProjectToDelete(null);
    },
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();

    if (!formData.project_name.trim() || !formData.project_number) {
      return;
    }

    createProjectMutation.mutate(formData);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setFormData({ project_name: "", project_number: "" });
  };

  const deleteProject = async () => {
    if (!projectToDelete) return;
    deleteProjectMutation.mutate(projectToDelete.id);
  };

  const filteredProjects = projects.filter((project) => {
    const matchesQuery = project.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    const matchesStatus =
      activeStatus === "All" || project.status === activeStatus;

    return matchesQuery && matchesStatus;
  });

  const stats = {
    total: projects.length,
    active: projects.filter((p) => p.status === "Active").length,
    pending: projects.filter((p) => p.status === "Pending").length,
    completed: projects.filter((p) => p.status === "Completed").length,
  };

  return (
    <MainLayout location={location} setLocation={setLocation}>
      <div
        className={`bg-gray-50 min-h-full py-4 px-6 transition duration-300 ${
          deleteModalOpen ? "blur-sm pointer-events-none select-none" : ""
        }`}
      >
          {!loading && !isError && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
            >
              {/* Total Projects Card */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">
                      Total Projects
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      <AnimatedCounter value={stats.total} />
                    </p>
                  </div>
                  <div className="relative">
                    <CircularProgress
                      percentage={100}
                      color="#3b82f6"
                      size={60}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-blue-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Projects Card */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">
                      Active Projects
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      <AnimatedCounter value={stats.active} />
                    </p>
                    <p className="text-xs text-blue-600 font-medium mt-1">
                      {stats.total > 0
                        ? Math.round((stats.active / stats.total) * 100)
                        : 0}
                      % of total
                    </p>
                  </div>
                  <div className="relative">
                    <CircularProgress
                      percentage={
                        stats.total > 0 ? (stats.active / stats.total) * 100 : 0
                      }
                      color="#3b82f6"
                      size={60}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-blue-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pending Projects Card */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">
                      Pending Projects
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      <AnimatedCounter value={stats.pending} />
                    </p>
                    <p className="text-xs text-blue-600 font-medium mt-1">
                      {stats.total > 0
                        ? Math.round((stats.pending / stats.total) * 100)
                        : 0}
                      % of total
                    </p>
                  </div>
                  <div className="relative">
                    <CircularProgress
                      percentage={
                        stats.total > 0
                          ? (stats.pending / stats.total) * 100
                          : 0
                      }
                      color="#3b82f6"
                      size={60}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-blue-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Completed Projects Card */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">
                      Completed Projects
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      <AnimatedCounter value={stats.completed} />
                    </p>
                    <p className="text-xs text-blue-600 font-medium mt-1">
                      {stats.total > 0
                        ? Math.round((stats.completed / stats.total) * 100)
                        : 0}
                      % of total
                    </p>
                  </div>
                  <div className="relative">
                    <CircularProgress
                      percentage={
                        stats.total > 0
                          ? (stats.completed / stats.total) * 100
                          : 0
                      }
                      color="#3b82f6"
                      size={60}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-blue-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
            <div className="flex gap-2">
              {["All", "Active", "Pending", "Completed"].map((status) => (
                <button
                  key={status}
                  onClick={() => setActiveStatus(status)}
                  className={`px-3 py-2 rounded-md border text-sm shadow-sm transition ${
                    activeStatus === status
                      ? "bg-blue-100 text-blue-800 border border-blue-200"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white text-gray-900 placeholder-gray-400 border border-gray-300 rounded-md px-4 py-2 max-w-xs w-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-md px-3 py-2 flex items-center gap-1 transition-colors duration-200 shadow-sm text-sm"
                title="Create New Project"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                Add Project
              </button>
            </div>
          </div>

          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-500">Loading projects...</span>
            </div>
          )}
          {isError && (
            <div className="text-red-600 text-center bg-red-50 p-4 rounded-lg border border-red-200">
              {error || "Failed to load projects."}
            </div>
          )}
          {!loading && !isError && filteredProjects.length === 0 && (
            <div className="text-gray-500 text-center">No projects found.</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            <AnimatePresence>
              {filteredProjects.map((project) => (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  onMouseEnter={() => onHoverLoadProjectDetail(project.id)}
                  onClick={() => navigate(`/projects/${project.id}`)}
                  className="bg-white rounded-lg border border-gray-200 md:hover:bg-gray-50 cursor-pointer"
                >
                  <div className="p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-medium text-lg text-gray-900">
                        {project.name}
                      </h3>
                      <ProjectDropdown
                        project={project}
                        onDelete={() => {
                          setProjectToDelete(project);
                          setDeleteModalOpen(true);
                        }}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mb-3">
                      {project.project_code
                        ? `Project Code: ${project.project_code}`
                        : `Project ID: PRJ-${project.user_project_number}`}
                    </p>
                    <div className="mb-4">
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded-full bg-blue-100 text-blue-800 border border-blue-200`}
                      >
                        {project.status || "undefined"}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          Start Date
                        </p>
                        <p className="font-medium text-gray-900">
                          {project.startDate ?? "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          Deadline
                        </p>
                        <p className="font-medium text-gray-900">
                          {project.deadline ?? "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          Budget
                        </p>
                        <p className="font-medium text-gray-900">
                          {project.budget ?? "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 pb-4 pt-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/projects/${project.id}`);
                      }}
                      className="w-full border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-md text-sm transition shadow-sm"
                    >
                      Details
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
      </div>

      {deleteModalOpen && (
        <motion.div
          key="editModalOverlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12, ease: "easeOut" }}
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
        >
          <motion.div
            key="editModalContent"
            initial={{ opacity: 0, y: 10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.97 }}
            transition={{ duration: 0.16, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg border border-gray-200 shadow-xl p-6 w-full max-w-lg"
          >
            <h2 className="text-xl font-medium mb-4 text-gray-900">
              Confirm Deletion
            </h2>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold">{projectToDelete?.name}</span>?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setProjectToDelete(null);
                }}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={deleteProject}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 shadow-sm"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg border border-gray-200 shadow-xl max-w-md w-full p-6"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-medium text-gray-900">
                  Create New Project
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreateProject} className="space-y-4">
                <div>
                  <label
                    htmlFor="project_name"
                    className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2"
                  >
                    Project Name
                  </label>
                  <input
                    type="text"
                    id="project_name"
                    name="project_name"
                    value={formData.project_name}
                    onChange={handleInputChange}
                    placeholder="Enter project name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    disabled={createProjectMutation.isPending}
                  />
                </div>

                <div>
                  <label
                    htmlFor="project_number"
                    className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2"
                  >
                    Project Number
                  </label>
                  <input
                    type="number"
                    id="project_number"
                    name="project_number"
                    value={formData.project_number}
                    onChange={handleInputChange}
                    placeholder="Enter project number"
                    min="1"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    disabled={createProjectMutation.isPending}
                  />
                </div>

                {createProjectMutation.error && (
                  <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
                    {createProjectMutation.error.response?.status === 409
                      ? "This project name is already in use."
                      : createProjectMutation.error.response?.status === 400
                      ? "Project number already exists for this user or invalid data provided."
                      : "Failed to create project. Please try again."}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors shadow-sm"
                    disabled={createProjectMutation.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      createProjectMutation.isPending ||
                      !formData.project_name.trim() ||
                      !formData.project_number
                    }
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-sm"
                  >
                    {createProjectMutation.isPending
                      ? "Creating..."
                      : "Create Project"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </MainLayout>
  );
}

// Old Version
// import { useAuth } from "../context/AuthContext.js";
// import { useNavigate } from "react-router-dom";
// import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
// import { useState } from "react";
// import api from "../api/api.js";
// import MainLayout from "../sidebar/Sidebar.js";
// import { motion, AnimatePresence } from "framer-motion";
// import { ProjectDropdown } from "../components/ProjectDropdown.js";

// export default function ProjectsList() {
//   const { user } = useAuth();
//   const navigate = useNavigate();
//   const queryClient = useQueryClient();

//   const [location, setLocation] = useState("Projects");
//   const [searchQuery, setSearchQuery] = useState("");
//   const [activeStatus, setActiveStatus] = useState("All");
//   const [deleteModalOpen, setDeleteModalOpen] = useState(false);
//   const [projectToDelete, setProjectToDelete] = useState(null);
//   const [showCreateModal, setShowCreateModal] = useState(false);
//   const [formData, setFormData] = useState({
//     project_name: "",
//     project_number: "",
//   });

//   const [error, setError] = useState(null);

//   const {
//     data: projects = [],
//     isLoading: loading,
//     isError,
//   } = useQuery({
//     queryKey: ["projects"],
//     queryFn: async () => {
//       const response = await api.get("/projects/");
//       console.log(response.data.length);
//       return response.data;
//     },
//     enabled: !!user,
//     staleTime: 5 * 60 * 1000, // 5 minutes
//     cacheTime: 10 * 60 * 1000, // 10 minutes
//   });

//   const createProjectMutation = useMutation({
//     mutationFn: async (projectData) => {
//       const response = await api.post("/projects/", {
//         project_name: projectData.project_name.trim(),
//         project_number: Number.parseInt(projectData.project_number),
//       });
//       return response.data;
//     },
//     onSuccess: (newProject) => {
//       queryClient.invalidateQueries(["projects"]);

//       queryClient.setQueryData(["projects"], (oldProjects) => [
//         newProject,
//         ...(oldProjects || []),
//       ]);

//       setFormData({ project_name: "", project_number: "" });
//       setShowCreateModal(false);
//     },
//     onError: (err) => {
//       if (err.response?.status === 409) {
//         setError("This project name is already in use.");
//       } else if (err.response?.status === 400) {
//         setError(
//           "Project number already exists for this user or invalid data provided."
//         );
//       } else {
//         setError("Failed to create project. Please try again.");
//       }
//     },
//   });

//   function onHoverLoadProjectDetail(projectId) {
//     // Prefetch line items
//     queryClient.prefetchQuery({
//       queryKey: ["lineItems", projectId],
//       queryFn: async () => {
//         const response = await api.get(`/projects/${projectId}/line-items/`);
//         return response.data;
//       },
//       staleTime: 5 * 60 * 1000,
//     });

//     // Prefetch todos
//     queryClient.prefetchQuery({
//       queryKey: ["projectTodos", projectId],
//       queryFn: async () => {
//         const response = await api.get(`/todos/?project_id=${projectId}`);
//         return response.data;
//       },
//       staleTime: 5 * 60 * 1000,
//     });
//   }

//   const deleteProjectMutation = useMutation({
//     mutationFn: async (projectId) => {
//       await api.delete(`/projects/${projectId}`);
//       return projectId;
//     },
//     onSuccess: (deletedProjectId) => {
//       queryClient.invalidateQueries(["projects"]);

//       queryClient.setQueryData(["projects"], (oldProjects) =>
//         (oldProjects || []).filter((proj) => proj.id !== deletedProjectId)
//       );
//       setDeleteModalOpen(false);
//       setProjectToDelete(null);
//     },
//     onError: (error) => {
//       console.error("Failed to delete project:", error);
//       alert("Could not delete the project. Please try again.");
//       setDeleteModalOpen(false);
//       setProjectToDelete(null);
//     },
//   });

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({
//       ...prev,
//       [name]: value,
//     }));
//   };

//   const handleCreateProject = async (e) => {
//     e.preventDefault();

//     if (!formData.project_name.trim() || !formData.project_number) {
//       return;
//     }

//     createProjectMutation.mutate(formData);
//   };

//   const closeModal = () => {
//     setShowCreateModal(false);
//     setFormData({ project_name: "", project_number: "" });
//   };

//   const statusColors = {
//     All: "bg-gray-200 text-gray-800 border-gray-400",
//     Active: "bg-green-100 text-green-700 border-green-300",
//     Pending: "bg-yellow-100 text-yellow-700 border-yellow-300",
//     Completed: "bg-blue-100 text-blue-700 border-blue-300",
//   };

//   const deleteProject = async () => {
//     if (!projectToDelete) return;
//     deleteProjectMutation.mutate(projectToDelete.id);
//   };

//   const filteredProjects = projects.filter((project) => {
//     const matchesQuery = project.name
//       .toLowerCase()
//       .includes(searchQuery.toLowerCase());

//     const matchesStatus =
//       activeStatus === "All" || project.status === activeStatus;

//     return matchesQuery && matchesStatus;
//   });

//   return (
//     <MainLayout location={location} setLocation={setLocation}>
//       <div
//         className={`bg-gray-50 py-6 transition duration-300 ${
//           deleteModalOpen ? "blur-sm pointer-events-none select-none" : ""
//         }`}
//       >
//         <div className="max-w-6xl mx-auto">
//           <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
//             <div className="flex gap-3">
//               {["All", "Active", "Pending", "Completed"].map((status) => (
//                 <button
//                   key={status}
//                   onClick={() => setActiveStatus(status)}
//                   className={`px-4 py-2 rounded-full border transition ${
//                     activeStatus === status
//                       ? statusColors[status]
//                       : "border-gray-300 text-gray-700 hover:bg-gray-100"
//                   }`}
//                 >
//                   {status}
//                 </button>
//               ))}
//             </div>
//             <div className="flex items-center gap-3">
//               <input
//                 type="text"
//                 placeholder="Search projects..."
//                 value={searchQuery}
//                 onChange={(e) => setSearchQuery(e.target.value)}
//                 className="bg-gray-100 text-gray-900 placeholder-gray-400 rounded-md px-4 py-2 max-w-xs w-full focus:outline-none focus:ring-2 focus:ring-green-500"
//               />
//               <button
//                 onClick={() => setShowCreateModal(true)}
//                 className="bg-green-600 hover:bg-green-700 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors duration-200 shadow-md hover:shadow-lg"
//                 title="Create New Project"
//               >
//                 <svg
//                   className="w-5 h-5"
//                   fill="none"
//                   stroke="currentColor"
//                   viewBox="0 0 24 24"
//                 >
//                   <path
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                     strokeWidth={2}
//                     d="M12 6v6m0 0v6m0-6h6m-6 0H6"
//                   />
//                 </svg>
//               </button>
//             </div>
//           </div>

//           {loading && (
//             <div className="flex justify-center items-center py-12">
//               <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
//               <span className="ml-3 text-gray-500">Loading projects...</span>
//             </div>
//           )}
//           {isError && (
//             <div className="text-red-600 text-center bg-red-50 p-4 rounded-lg">
//               {error || "Failed to load projects."}
//             </div>
//           )}
//           {!loading && !isError && filteredProjects.length === 0 && (
//             <div className="text-gray-500 text-center">No projects found.</div>
//           )}

//           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
//             <AnimatePresence>
//               {filteredProjects.map((project) => (
//                 <motion.div
//                   key={project.id}
//                   layout
//                   initial={{ opacity: 0, scale: 0.9 }}
//                   animate={{ opacity: 1, scale: 1 }}
//                   exit={{ opacity: 0, scale: 0.9 }}
//                   transition={{ duration: 0.3 }}
//                   onMouseEnter={() => onHoverLoadProjectDetail(project.id)}
//                   onClick={() => navigate(`/projects/${project.id}`)}
//                   className="bg-white rounded-2xl shadow-md border border-gray-200 md:hover:border-green-500 md:hover:scale-105 md:hover:shadow-lg cursor-pointer"
//                 >
//                   <div className="p-8">
//                     <div className="flex justify-between items-center mb-3">
//                       <h3 className="font-bold text-xl text-gray-900">
//                         {project.name}
//                       </h3>
//                       <ProjectDropdown
//                         project={project}
//                         onDelete={() => {
//                           setProjectToDelete(project);
//                           setDeleteModalOpen(true);
//                         }}
//                       />
//                     </div>
//                     <p className="text-base text-gray-600">
//                       {project.project_code
//                         ? `Project Code: ${project.project_code}`
//                         : `Project ID: PRJ-${project.user_project_number}`}
//                     </p>
//                     <div className="mt-4">
//                       <span
//                         className={`text-sm font-semibold px-3 py-1 rounded-full border inline-block ${
//                           statusColors[project.status] ||
//                           "bg-gray-100 text-gray-700 border-gray-300"
//                         }`}
//                       >
//                         {project.status || "undefined"}
//                       </span>
//                     </div>
//                     <div className="flex justify-between text-base text-gray-600 mt-4">
//                       <div>
//                         <p className="text-gray-400">Start Date</p>
//                         <p className="font-medium">
//                           {project.startDate ?? "N/A"}
//                         </p>
//                       </div>
//                       <div>
//                         <p className="text-gray-400">Deadline</p>
//                         <p className="font-medium">
//                           {project.deadline ?? "N/A"}
//                         </p>
//                       </div>
//                       <div>
//                         <p className="text-gray-400">Budget</p>
//                         <p className="font-medium">{project.budget ?? "N/A"}</p>
//                       </div>
//                     </div>
//                   </div>
//                   <div className="flex justify-between px-8 pb-8 pt-0">
//                     <button
//                       onClick={(e) => {
//                         e.stopPropagation();
//                         navigate(`/projects/${project.id}`);
//                       }}
//                       className="border border-green-600 text-green-600 hover:bg-green-600 hover:text-white px-5 py-3 rounded-lg text-base transition"
//                     >
//                       Details
//                     </button>
//                   </div>
//                 </motion.div>
//               ))}
//             </AnimatePresence>
//           </div>
//         </div>
//       </div>

//       {deleteModalOpen && (
//         <motion.div
//           key="editModalOverlay"
//           initial={{ opacity: 0 }}
//           animate={{ opacity: 1 }}
//           exit={{ opacity: 0 }}
//           transition={{ duration: 0.12, ease: "easeOut" }}
//           className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
//         >
//           <motion.div
//             key="editModalContent"
//             initial={{ opacity: 0, y: 10, scale: 0.97 }}
//             animate={{ opacity: 1, y: 0, scale: 1 }}
//             exit={{ opacity: 0, y: 10, scale: 0.97 }}
//             transition={{ duration: 0.16, ease: "easeOut" }}
//             onClick={(e) => e.stopPropagation()}
//             className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg"
//           >
//             <h2 className="text-xl font-bold mb-4 text-gray-900">
//               Confirm Deletion
//             </h2>
//             <p className="text-gray-700 mb-6">
//               Are you sure you want to delete{" "}
//               <span className="font-semibold">{projectToDelete?.name}</span>?
//             </p>
//             <div className="flex justify-end gap-4">
//               <button
//                 onClick={() => {
//                   setDeleteModalOpen(false);
//                   setProjectToDelete(null);
//                 }}
//                 className="px-4 py-2 rounded-lg border text-gray-700 hover:bg-gray-100"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={deleteProject}
//                 className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
//               >
//                 Delete
//               </button>
//             </div>
//           </motion.div>
//         </motion.div>
//       )}

//       <AnimatePresence>
//         {showCreateModal && (
//           <motion.div
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//             className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
//             onClick={closeModal}
//           >
//             <motion.div
//               initial={{ scale: 0.9, opacity: 0 }}
//               animate={{ scale: 1, opacity: 1 }}
//               exit={{ scale: 0.9, opacity: 0 }}
//               onClick={(e) => e.stopPropagation()}
//               className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
//             >
//               <div className="flex justify-between items-center mb-6">
//                 <h2 className="text-2xl font-bold text-gray-900">
//                   Create New Project
//                 </h2>
//                 <button
//                   onClick={closeModal}
//                   className="text-gray-400 hover:text-gray-600 transition-colors"
//                 >
//                   <svg
//                     className="w-6 h-6"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M6 18L18 6M6 6l12 12"
//                     />
//                   </svg>
//                 </button>
//               </div>

//               <form onSubmit={handleCreateProject} className="space-y-4">
//                 <div>
//                   <label
//                     htmlFor="project_name"
//                     className="block text-sm font-medium text-gray-700 mb-2"
//                   >
//                     Project Name
//                   </label>
//                   <input
//                     type="text"
//                     id="project_name"
//                     name="project_name"
//                     value={formData.project_name}
//                     onChange={handleInputChange}
//                     placeholder="Enter project name"
//                     className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
//                     disabled={createProjectMutation.isPending}
//                   />
//                 </div>

//                 <div>
//                   <label
//                     htmlFor="project_number"
//                     className="block text-sm font-medium text-gray-700 mb-2"
//                   >
//                     Project Number
//                   </label>
//                   <input
//                     type="number"
//                     id="project_number"
//                     name="project_number"
//                     value={formData.project_number}
//                     onChange={handleInputChange}
//                     placeholder="Enter project number"
//                     min="1"
//                     className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
//                     disabled={createProjectMutation.isPending}
//                   />
//                 </div>

//                 {createProjectMutation.error && (
//                   <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">
//                     {createProjectMutation.error.response?.status === 409
//                       ? "This project name is already in use."
//                       : createProjectMutation.error.response?.status === 400
//                       ? "Project number already exists for this user or invalid data provided."
//                       : "Failed to create project. Please try again."}
//                   </div>
//                 )}

//                 <div className="flex gap-3 pt-2">
//                   <button
//                     type="button"
//                     onClick={closeModal}
//                     className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
//                     disabled={createProjectMutation.isPending}
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     type="submit"
//                     disabled={
//                       createProjectMutation.isPending ||
//                       !formData.project_name.trim() ||
//                       !formData.project_number
//                     }
//                     className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
//                   >
//                     {createProjectMutation.isPending
//                       ? "Creating..."
//                       : "Create Project"}
//                   </button>
//                 </div>
//               </form>
//             </motion.div>
//           </motion.div>
//         )}
//       </AnimatePresence>
//     </MainLayout>
//   );
// }
